import { useEffect, useState } from "react";
import { listSwitchLogs, type SwitchLog, apiRequest } from "@/lib/api";
import { formatDate, formatRelative } from "@/lib/format";
import { X } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, type ColumnDef } from "@/components/DataTable";

export function SwitchLogsPage() {
  const [logs, setLogs] = useState<SwitchLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({ switch_name: "", direction: "", error_only: false });
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const params: any = { limit: 50 };
      if (!reset && cursor) params.cursor = cursor;
      if (filters.switch_name) params.switch_name = filters.switch_name;
      if (filters.direction) params.direction = filters.direction;
      if (filters.error_only) params.error_only = true;
      const r = await listSwitchLogs(params);
      setLogs(reset ? r.data : [...logs, ...r.data]);
      setCursor(r.next_cursor ?? null);
      setHasMore(r.has_more);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCursor(null);
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.switch_name, filters.direction, filters.error_only]);

  const openDetail = async (id: number) => {
    const full = await apiRequest<Record<string, unknown>>(`/admin/switch-logs/${id}`);
    setDetail(full);
  };

  const columns: ColumnDef<SwitchLog>[] = [
    {
      key: "date",
      header: "Date",
      minWidth: 100,
      cell: (l) => <span className="text-xs text-[#7e8299]" title={formatDate(l.created_at)}>{formatRelative(l.created_at)}</span>,
    },
    {
      key: "switch",
      header: "Switch",
      minWidth: 110,
      cell: (l) => <Badge color="info">{l.switch_name}</Badge>,
    },
    {
      key: "operation",
      header: "Opération",
      minWidth: 130,
      cell: (l) => <span className="text-sm font-medium">{l.operation}</span>,
    },
    {
      key: "direction",
      header: "Direction",
      minWidth: 110,
      cell: (l) => <Badge color={l.direction === "out" ? "primary" : "warning"}>{l.direction}</Badge>,
    },
    {
      key: "url",
      header: "URL",
      minWidth: 280,
      cell: (l) => (
        <span className="font-mono text-xs text-[#7e8299] block max-w-[360px] truncate" title={l.request_url ?? ""}>
          {l.request_url ?? "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "HTTP",
      align: "right",
      minWidth: 80,
      cell: (l) =>
        l.response_status ? (
          <Badge color={l.response_status < 400 ? "success" : "danger"}>{l.response_status}</Badge>
        ) : (
          <span className="text-[#7e8299]">—</span>
        ),
    },
    {
      key: "latency",
      header: "Latence",
      align: "right",
      minWidth: 90,
      cell: (l) => <span className="text-xs text-[#7e8299]">{l.duration_ms ? `${l.duration_ms}ms` : "—"}</span>,
    },
    {
      key: "payment",
      header: "Payment",
      minWidth: 140,
      cell: (l) => (
        <span className="font-mono text-xs text-[#7e8299]">{l.payment_id ? l.payment_id.slice(0, 14) + "…" : "—"}</span>
      ),
    },
  ];

  const filtersSlot = (
    <>
      <select value={filters.switch_name} onChange={(e) => setFilters({ ...filters, switch_name: e.target.value })} className="form-input py-2 px-3 text-sm w-auto">
        <option value="">Tous switches</option>
        <option value="mpesa">Mpesa</option>
        <option value="orange">Orange</option>
        <option value="airtel">Airtel</option>
        <option value="afrimoney">AfriMoney</option>
        <option value="cybersource">Cybersource</option>
      </select>
      <select value={filters.direction} onChange={(e) => setFilters({ ...filters, direction: e.target.value })} className="form-input py-2 px-3 text-sm w-auto">
        <option value="">Toutes directions</option>
        <option value="out">Outbound</option>
        <option value="in">Inbound (callback)</option>
      </select>
      <label className="text-sm text-[#3f4254] inline-flex items-center gap-2 ml-2">
        <input type="checkbox" checked={filters.error_only} onChange={(e) => setFilters({ ...filters, error_only: e.target.checked })} className="rounded" />
        Erreurs uniquement
      </label>
    </>
  );

  return (
    <div>
      <PageHeader
        title="Switch logs"
        breadcrumbs={[{ label: "Informatique" }, { label: "Switch logs" }]}
      />

      <DataTable<SwitchLog>
        title="Logs HTTP switches"
        subtitle="Requêtes/réponses vers les gateways telco et carte"
        columns={columns}
        data={logs}
        rowKey={(l) => l.id}
        loading={loading && logs.length === 0}
        filtersSlot={filtersSlot}
        onRowClick={(l) => openDetail(l.id)}
        loadMore={hasMore ? { hasMore, onLoadMore: () => load(false), loading } : undefined}
      />

      {detail && <DetailDrawer detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function DetailDrawer({ detail, onClose }: { detail: Record<string, unknown>; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-2xl bg-white h-screen overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white z-10 px-7 py-5 border-b border-[#eff2f5] flex items-center justify-between">
          <div>
            <div className="card-title">
              Log {String(detail.switch_name)} · {String(detail.operation)}
            </div>
            <div className="card-subtitle">id={String(detail.id)}</div>
          </div>
          <button onClick={onClose} className="btn btn-light btn-icon">
            <X size={18} />
          </button>
        </div>
        <div className="px-7 py-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Info label="Request ID" value={String(detail.request_id ?? "—")} mono />
            <Info label="HTTP status" value={String(detail.response_status ?? "—")} />
            <Info label="Latence" value={detail.duration_ms ? `${detail.duration_ms}ms` : "—"} />
            <Info label="Direction" value={String(detail.direction)} />
            <Info label="Payment" value={String(detail.payment_id ?? "—")} mono />
            <Info label="Refund" value={String(detail.refund_id ?? "—")} mono />
          </div>
          <Info label="URL" value={String(detail.request_url ?? "—")} mono full />

          {detail.request_body !== undefined && detail.request_body !== null && (
            <Block title="Request body" body={detail.request_body} />
          )}
          {detail.response_body !== undefined && detail.response_body !== null && (
            <Block title="Response body" body={detail.response_body} />
          )}
          {detail.error ? (
            <div>
              <div className="text-xs uppercase text-[#7e8299] tracking-wider mb-2 font-bold">Erreur</div>
              <div className="bg-danger-50 text-danger-700 rounded-lg p-4 text-sm">{String(detail.error)}</div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, mono, full }: { label: string; value: string; mono?: boolean; full?: boolean }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-xs uppercase text-[#7e8299] tracking-wider font-bold mb-1.5">{label}</div>
      <div className={`text-sm text-[#181c32] ${mono ? "font-mono break-all" : ""}`}>{value}</div>
    </div>
  );
}

function Block({ title, body }: { title: string; body: unknown }) {
  const text = typeof body === "string" ? body : JSON.stringify(body, null, 2);
  return (
    <div>
      <div className="text-xs uppercase text-[#7e8299] tracking-wider font-bold mb-2">{title}</div>
      <pre className="bg-[#f5f8fa] rounded-lg p-4 text-xs overflow-x-auto whitespace-pre-wrap break-all">{text}</pre>
    </div>
  );
}
